import { Button } from '@helvetia/components/Button'
import { Option } from '@helvetia/components/forms/NativeSelect/NativeSelect.types'
import NumberInput from '@helvetia/components/forms/NumberInput'
import { OptionSelectorField } from '@helvetia/components/forms/OptionSelector'
import { Col, Row } from '@helvetia/components/layout'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, FieldProps, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { LifecycleView } from '../../_components/ConnectComponent'
import { DropDownSelect } from '../../_components/DropDownSelect'
import { RadioGroup } from '../../_components/RadioGroup'
import { CalculatorStepper } from '../CaculatorStepper'
import { DeductiblePresenter } from './Deductible.presenter'
import {
  validateFieldForm,
  validateIndividualVehicleSum,
  validateSingleInsuredVehicle,
} from './Deductible.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface InsuredAndDeductibleValues {
  deductible: number
  sumInsuredType: string
  sumInsured: number
  insuredVehicle1: number
  insuredVehicle2: number
  insuredVehicle3: number
  insuredVehicle4: number
  insuredVehicle5: number
}

interface Props {
  presenter: DeductiblePresenter
  onSubmit: (values: InsuredAndDeductibleValues) => void
}

interface State {
  initialValues?: InsuredAndDeductibleValues
  isEditing: boolean
  vehiclesToEnsure: number
  isLoading: boolean
  platesArray: number[]
}

const deductibleOptions: Option[] = [
  { text: '300', value: 300 },
  { text: '500', value: 500 },
  { text: '1000', value: 1000 },
  { text: '2000', value: 2000 },
  { text: '3000', value: 3000 },
  { text: '5000', value: 5000 },
]

export class DeductibleSection
  extends React.Component<
    Props & FieldProps & RouteComponentProps & WithNamespaces,
    State
  >
  implements View {
  private presenter: DeductiblePresenter
  private radioValues

  constructor(props) {
    super(props)

    this.radioValues = [
      {
        text: `${props.t('step2.sumInsured.same')}`,
        value: 'same',
      },
      {
        text: `${props.t('step2.sumInsured.different')}`,
        value: 'different',
      },
    ]

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.state = {
      initialValues: null,
      isEditing: false,
      vehiclesToEnsure: 0,
      isLoading: false,
      platesArray: [],
    }
  }

  setInitialValues = (
    initialValues,
    platesArray,
    vehicles,
    isEditing: boolean,
  ) => {
    this.setState({
      initialValues,
      platesArray,
      vehiclesToEnsure: vehicles,
      isEditing,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickContinuar = (values: InsuredAndDeductibleValues) =>
    this.presenter.onContinue(values)

  handleBackClick = () => {
    this.presenter.onBackClick()
  }

  showMultipleInsuredSumInputs = (setFieldValue, values) => {
    const { t } = this.props

    return (
      <Row marginTop="xs">
        {this.state.platesArray.map(
          (plate, index) =>
            plate && (
              <Col
                mobile={12}
                tablet={6}
                desktop={6}
                verticalGutter
                key={index}
              >
                <Field
                  name={`insuredVehicle${index + 1}`}
                  label={`${t('step2.insuredValue.different')} ${plate}`}
                  component={NumberInput}
                  onInputChange={event => {
                    const insuredValue = Number(event.currentTarget.value)
                    values.sumPerVehicle[index] = insuredValue
                    setFieldValue(`insuredVehicle${index + 1}`, insuredValue)
                  }}
                  validate={validateIndividualVehicleSum(
                    values[`insuredVehicle${index + 1}`],
                  )}
                />
              </Col>
            ),
        )}
      </Row>
    )
  }

  render() {
    const { t } = this.props
    const { isEditing } = this.state
    if (!this.state.initialValues) return <Spinner size="xl" />
    return (
      <>
        <Row>
          <Col tablet={12} desktop={4} verticalGutter>
            <CalculatorStepper step={2} />
          </Col>
          <Col tablet={12} desktop={8}>
            <Row>
              <Text as="p" color="black" size="l">
                {t('step2.title')}
              </Text>
            </Row>

            <Formik
              initialValues={this.state.initialValues}
              onSubmit={this.handleClickContinuar}
              validate={values =>
                validateFieldForm(values, this.state.vehiclesToEnsure)
              }
              render={({ setFieldValue, setValues, values }) => (
                <Form>
                  {this.state.vehiclesToEnsure === 1 ? (
                    <Row marginTop="xxs">
                      <Col>
                        <Field
                          name={`sumInsured`}
                          label={`${t('step2.insuredValue.different')} ${
                            this.state.platesArray[0]
                          }`}
                          component={NumberInput}
                          onInputChange={event => {
                            const insuredValue = Number(
                              event.currentTarget.value,
                            )
                            setFieldValue('sumInsuredType', 'same')
                            setFieldValue('sumInsured', insuredValue)
                            setFieldValue('insuredVehicle1', insuredValue)
                          }}
                          validate={() =>
                            validateSingleInsuredVehicle(
                              values['insuredVehicle1'],
                            )
                          }
                        />
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Field
                        component={RadioGroup}
                        name="sumInsuredType"
                        questionText={t('step2.sumInsured.question')}
                        radioList={this.radioValues}
                        defaultSelected={values.sumInsuredType}
                        onChange={event =>
                          setValues({
                            ...values,
                            sumInsuredType: event.currentTarget.value,
                          })
                        }
                      />

                      {values.sumInsuredType === 'same' && (
                        <Row marginTop="xxs">
                          <Col>
                            <Field
                              component={NumberInput}
                              name="sumInsured"
                              label={t('step2.insuredValue.same')}
                              onInputChange={event => {
                                const insuredValue = Number(
                                  event.currentTarget.value,
                                )

                                this.state.platesArray.map((_, index) => {
                                  setFieldValue(
                                    `insuredVehicle${index + 1}`,
                                    insuredValue,
                                  )
                                })
                                values.sumInsured = insuredValue
                              }}
                            />
                          </Col>
                        </Row>
                      )}

                      {values.sumInsuredType === 'different' &&
                        this.showMultipleInsuredSumInputs(
                          setFieldValue,
                          values,
                        )}
                    </>
                  )}

                  <Row marginTop="s" marginBottom="s">
                    <Col>
                      <MatchMedia onlyMobile>
                        <Field
                          defaultSelected={values.deductible}
                          name="deductible"
                          label={t('step2.deductible.label')}
                          question={t('step2.deductible.question')}
                          options={deductibleOptions}
                          component={DropDownSelect}
                        />
                      </MatchMedia>
                      <MatchMedia tablet>
                        <Field
                          name="deductible"
                          textLabel={t('step2.deductible.question')}
                          optionList={deductibleOptions}
                          component={OptionSelectorField}
                        />
                      </MatchMedia>
                    </Col>
                  </Row>

                  <Row verticalGutter marginBottom="m">
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button primary type="submit">
                        {isEditing ? t('buttons.save') : t('buttons.continue')}
                      </Button>
                    </Col>
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button grey onClick={this.handleBackClick}>
                        {isEditing ? t('buttons.cancel') : t('buttons.back')}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const DeductibleView = withNamespaces()(withRouter(DeductibleSection))
